import React, { useState, useEffect, useCallback, useMemo } from "react";
import { get, post, patch, del } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  Card, CardBody, Row, Col, Table, Button, Spinner,
  Modal, ModalHeader, ModalBody, Form, Label, Input, Alert, Badge,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TURKEY_PROVINCES from "./turkeyProvinceList";

const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";

const EMPTY = {
  Province_Code: "",
  Province_DealerName: "",
  Province_ContactNumber: "",
  Province_IsHeadquarter: false,
  Province_IsActive: true,
};

export default function DealerProvincePage() {
  document.title = "Bayi Haritası (Türkiye) | BUHARA - Admin Paneli";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [notReady, setNotReady] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(`${ODATA_BASE}/Dealer_Province`);
      setRows(res?.value || []);
      setNotReady(false);
    } catch (err) {
      console.error("Dealer_Province fetch error:", err);
      setNotReady(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const provinceName = (code) =>
    TURKEY_PROVINCES.find(p => p.code === code)?.name || code;

  // Zaten eklenmiş iller listede tekrar seçilemesin (düzenlemede kendisi hariç)
  const usedCodes = useMemo(
    () => new Set(rows.filter(r => r.ID !== editId).map(r => r.Province_Code)),
    [rows, editId]
  );
  const selectableProvinces = useMemo(
    () => TURKEY_PROVINCES.filter(p => !usedCodes.has(p.code)),
    [usedCodes]
  );

  const openAdd = () => { setEditId(null); setForm(EMPTY); setModal(true); };

  const openEdit = (row) => {
    setEditId(row.ID);
    setForm({
      Province_Code: row.Province_Code || "",
      Province_DealerName: row.Province_DealerName || "",
      Province_ContactNumber: row.Province_ContactNumber || "",
      Province_IsHeadquarter: !!row.Province_IsHeadquarter,
      Province_IsActive: row.Province_IsActive !== false,
    });
    setModal(true);
  };

  const handleField = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.Province_Code) {
      toastr.warning("Lütfen bir il seçin.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        Province_Code: form.Province_Code,
        Province_Name: provinceName(form.Province_Code),
        Province_DealerName: form.Province_DealerName || null,
        Province_ContactNumber: form.Province_ContactNumber || null,
        Province_IsHeadquarter: !!form.Province_IsHeadquarter,
        Province_IsActive: !!form.Province_IsActive,
      };
      if (editId) {
        await patch(`${ODATA_BASE}/Dealer_Province(${editId})`, payload);
        toastr.success("İl güncellendi.");
      } else {
        await post(`${ODATA_BASE}/Dealer_Province`, payload);
        toastr.success("İl haritaya eklendi.");
      }
      setModal(false);
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`${provinceName(row.Province_Code)} ilini haritadan kaldırmak istiyor musunuz?`)) return;
    try {
      await del(`${ODATA_BASE}/Dealer_Province(${row.ID})`);
      toastr.success("İl haritadan kaldırıldı.");
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Silme sırasında hata oluştu.");
    }
  };

  // Listeyi il adına göre sırala, ana üretim tesisi en üstte
  const sortedRows = [...rows].sort((a, b) => {
    if (!!b.Province_IsHeadquarter !== !!a.Province_IsHeadquarter) {
      return b.Province_IsHeadquarter ? 1 : -1;
    }
    return provinceName(a.Province_Code).localeCompare(provinceName(b.Province_Code), "tr");
  });

  const activeCount = rows.filter(r => r.Province_IsActive !== false).length;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Bayi Haritası (Türkiye)" />

        {notReady && (
          <Alert color="warning">
            <strong>Bayi haritası servisi henüz aktif değil.</strong><br />
            Backend'de <code>Dealer_Province</code> tablosu oluşturulup API yeniden
            yayınlandıktan sonra bu ekran çalışacaktır. Site bu süre boyunca
            mevcut il listesini göstermeye devam eder.
          </Alert>
        )}

        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h5 className="mb-1">Türkiye Bayi Haritası</h5>
                    <p className="text-muted mb-0">
                      Buradaki iller, web sitesindeki <em>Bayilerimiz</em> sayfasının
                      Türkiye haritasında renkli olarak işaretlenir.
                      {" "}<Badge color="success" pill>{activeCount}</Badge> il yayında.
                    </p>
                  </div>
                  <Button color="success" onClick={openAdd}>
                    <i className="bx bx-plus me-1" /> İl Ekle
                  </Button>
                </div>

                <p className="text-muted font-size-12 mb-3">
                  <i className="bx bx-info-circle me-1" />
                  <strong>Ana üretim tesisi</strong> işaretli il haritada yeşil renk ve
                  Buhara logosuyla, diğerleri altın rengiyle gösterilir.
                </p>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>İl</th>
                          <th>Plaka Kodu</th>
                          <th>Bayi Adı</th>
                          <th>İletişim</th>
                          <th>Tip</th>
                          <th>Durum</th>
                          <th className="text-end">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-4">
                              Henüz il eklenmemiş. &quot;İl Ekle&quot; ile başlayın.
                            </td>
                          </tr>
                        ) : sortedRows.map((row, i) => (
                          <tr key={row.ID}>
                            <td>{i + 1}</td>
                            <td className="fw-bold">{provinceName(row.Province_Code)}</td>
                            <td><code>{row.Province_Code}</code></td>
                            <td>{row.Province_DealerName || "-"}</td>
                            <td>{row.Province_ContactNumber || "-"}</td>
                            <td>
                              {row.Province_IsHeadquarter
                                ? <Badge color="success">Ana Üretim Tesisi</Badge>
                                : <Badge color="warning">Bayi</Badge>}
                            </td>
                            <td>
                              {row.Province_IsActive !== false
                                ? <Badge color="success" pill>Yayında</Badge>
                                : <Badge color="secondary" pill>Gizli</Badge>}
                            </td>
                            <td className="text-end">
                              <Button color="link" className="text-success p-0 me-3" onClick={() => openEdit(row)}>
                                <i className="mdi mdi-pencil font-size-18" />
                              </Button>
                              <Button color="link" className="text-danger p-0" onClick={() => handleDelete(row)}>
                                <i className="mdi mdi-delete font-size-18" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={() => setModal(false)}>
          <ModalHeader toggle={() => setModal(false)} tag="h4">
            {editId ? "İl Düzenle" : "Haritaya İl Ekle"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSave}>
              <div className="mb-3">
                <Label className="form-label">İl</Label>
                <Input
                  type="select"
                  name="Province_Code"
                  value={form.Province_Code}
                  onChange={handleField}
                  required
                >
                  <option value="">— İl seçin —</option>
                  {editId && form.Province_Code && (
                    <option value={form.Province_Code}>
                      {provinceName(form.Province_Code)}
                    </option>
                  )}
                  {selectableProvinces.map(p => (
                    <option key={p.code} value={p.code}>{p.name}</option>
                  ))}
                </Input>
                <div className="form-text">Haritada zaten ekli iller listede görünmez.</div>
              </div>

              <div className="mb-3">
                <Label className="form-label">Bayi Adı (opsiyonel)</Label>
                <Input
                  name="Province_DealerName"
                  value={form.Province_DealerName}
                  onChange={handleField}
                  placeholder="Örn. Buhara Konya Bayi"
                />
              </div>

              <div className="mb-3">
                <Label className="form-label">İletişim Numarası (opsiyonel)</Label>
                <Input
                  name="Province_ContactNumber"
                  value={form.Province_ContactNumber}
                  onChange={handleField}
                />
              </div>

              <div className="form-check mb-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="hq"
                  name="Province_IsHeadquarter"
                  checked={form.Province_IsHeadquarter}
                  onChange={handleField}
                />
                <Label className="form-check-label" for="hq">
                  Ana üretim tesisi (haritada yeşil + logo)
                </Label>
              </div>

              <div className="form-check mb-3">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="active"
                  name="Province_IsActive"
                  checked={form.Province_IsActive}
                  onChange={handleField}
                />
                <Label className="form-check-label" for="active">
                  Sitede yayında
                </Label>
              </div>

              <div className="text-end">
                <Button type="button" color="light" className="me-2" onClick={() => setModal(false)}>
                  İptal
                </Button>
                <Button type="submit" color="success" disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-1" /> : null}
                  {editId ? "Güncelle" : "Kaydet"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
